import { z } from 'zod';
import { pageCursorSchema, pageMetaSchema } from './common.js';

/**
 * Wire DTOs for the ERP register import epic's Wave 3 T9 endpoints
 * (SIL-269, resources/plans/erp-register-import-spec.md decision 9 —
 * coverage stat + gap list — and decision 8 — post-promote policy
 * suggestion). Both are READ-ONLY aggregations: no write DTO exists here by
 * design (spec 3.2 "GET is read-only aggregation; the banner's apply calls
 * the EXISTING policy endpoints from the UI — no new server-side
 * apply-suggestion write path").
 */

// ---------------------------------------------------------------------------
// Coverage — "N of M assets have financial data" (decision 9)
// ---------------------------------------------------------------------------

/** One asset missing a financial-data row (`asset_financial`) — the
 * "filtered gap list" decision 9 calls for. */
export const erpCoverageGapAssetSchema = z.object({
  assetId: z.string().uuid(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  serialNumber: z.string().nullable(),
  assetTag: z.string().nullable(),
});
export type ErpCoverageGapAsset = z.infer<typeof erpCoverageGapAssetSchema>;

/** GET /financials/coverage — org-wide stat, optionally narrowed to one
 * asset class via `?assetClassId=`. */
export const erpCoverageResponseSchema = z.object({
  totalAssets: z.number().int().nonnegative(),
  assetsWithFinancialData: z.number().int().nonnegative(),
  gaps: z.array(erpCoverageGapAssetSchema),
  pageMeta: pageMetaSchema,
  generatedAt: z.string(),
});
export type ErpCoverageResponse = z.infer<typeof erpCoverageResponseSchema>;

export const erpCoverageQuerySchema = z.object({
  assetClassId: z.string().min(1).optional(),
  cursor: pageCursorSchema.optional(),
  limit: z.coerce.number().int().positive().max(500).optional(),
});
export type ErpCoverageQuery = z.infer<typeof erpCoverageQuerySchema>;

// ---------------------------------------------------------------------------
// Policy suggestion — post-promote dominant-method/life aggregation vs the
// configured capitalization_policy (decision 8).
// ---------------------------------------------------------------------------

/** Normalized depreciation-method bucket an ERP's free-text
 * `erp_reported_method` is classified into for aggregation purposes only —
 * NEVER written back to `capitalization_policy` (spec 3.1: reported facts
 * only). `other` covers any free-text value this normalizer can't confidently
 * bucket (e.g. a vendor-specific code) — it still counts toward sampleSize
 * but never toward a method disagreement, since we're not confident what the
 * ERP actually meant. */
export const erpReportedMethodBucketSchema = z.enum([
  'straight_line',
  'declining_balance',
  'sum_of_years_digits',
  'other',
]);
export type ErpReportedMethodBucket = z.infer<typeof erpReportedMethodBucketSchema>;

export const erpPolicySuggestionClassSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  /** Number of promoted `asset_financial` rows in this class carrying a
   * non-null `erp_reported_method` or `erp_reported_life_months` — the
   * suggestion's sample size, so the UI can caveat a suggestion from a
   * single row differently than one from hundreds. */
  sampleSize: z.number().int().nonnegative(),
  dominantErpMethodRaw: z.string().nullable(),
  dominantErpMethodBucket: erpReportedMethodBucketSchema.nullable(),
  dominantErpLifeMonths: z.number().int().positive().nullable(),
  configuredMethod: z.enum(['declining_balance', 'straight_line', 'sum_of_years_digits']).nullable(),
  configuredUsefulLifeYears: z.number().int().positive().nullable(),
  configuredPolicyTier: z.enum(['class_override', 'org_default', 'platform_default', 'no_policy']),
  disagreesOnMethod: z.boolean(),
  disagreesOnLife: z.boolean(),
});
export type ErpPolicySuggestionClass = z.infer<typeof erpPolicySuggestionClassSchema>;

/** GET /financials/policy/suggestions. Only classes with a non-empty sample
 * (`sampleSize > 0`) are returned — a class with no ERP-reported facts yet
 * has nothing to suggest. */
export const erpPolicySuggestionResponseSchema = z.object({
  classes: z.array(erpPolicySuggestionClassSchema),
  generatedAt: z.string(),
});
export type ErpPolicySuggestionResponse = z.infer<typeof erpPolicySuggestionResponseSchema>;
